import {
  AutoSaveFeedback,
  ButtonV2,
  LabraToolTipV2,
} from '@labrav/react-components'
import {
  footerStateType,
  FooterV2,
} from '../../../common/components/FooterV2/FooterV2'
import { useStyles } from './FlyOutOnboardingFooter.styles'
import { FlyoutCloudSettingsSectionsProps } from '../../../modules/flyOutOnboarding/types'
import { FormikProps } from 'formik'
import { useDispatch, useSelector } from '../../../store'
import { sendMarketplaceOnboardingData } from '../../../modules/flyOutOnboarding/action'
import { useCallback, useEffect, useRef } from 'react'
import { autoSave, AutoSaveTypes, useAccountId } from '@labrav/common'
import debounce from 'lodash/debounce'
import { isEmpty, isEqual } from 'lodash'
import { CloudType } from '../../../types'
import { LockMessageState } from '../FlyOutAwsOnboarding/LockMessageState/LockMessageState'
import { useLocation, useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { DateTime } from 'luxon'
import {
  LockMetadata,
  MicrosoftOnboarding,
} from '../../../modules/flyOutOnboarding/types'
import { ConnectCloudAccountRef } from '../FlyOutMicrosoftCloudSettings/MicrosoftCloudSettingsSections/ConnectCloudAccount/ConnectCloudAccount'
import { touchCloudCredentialFieldsWithErrors } from '../../../modules/flyOutOnboarding/utils'

export const FlyOutOnboardingFooter: React.FC<{
  accountId?: string
  lockStatus: string
  setLockStatus: React.Dispatch<React.SetStateAction<string>>
  userProfileId: string
  latestLockMetadata: LockMetadata
  formikProps: FormikProps<FlyoutCloudSettingsSectionsProps>
  isFormLocked: boolean
  disableTooltipMsg: string
  setExpandedSections: React.Dispatch<
    React.SetStateAction<{
      prerequisites: boolean
      accountConfiguration: boolean
      cloudCredentials: boolean
    }>
  >
  connectCloudAccountRef?: React.RefObject<ConnectCloudAccountRef>
}> = ({
  accountId: accountIdFromProps,
  lockStatus,
  setLockStatus,
  userProfileId,
  latestLockMetadata,
  formikProps,
  isFormLocked,
  disableTooltipMsg,
  setExpandedSections,
  connectCloudAccountRef,
}) => {
  const { classes } = useStyles({ isFormLocked })
  const { values } = formikProps
  const accountIdFromState = useAccountId({
    product: 'flyout',
    cloud: CloudType.AZURE,
  })
  const accountId = accountIdFromProps || accountIdFromState
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const autoSaveStatus = useSelector<{ status: AutoSaveTypes; error?: string }>(
    state => state.autoSave?.flyOutMicrosoftOnboarding ?? { status: 'standBy' }
  )
  const isFirstRender = useRef(true)
  const microsoftOnboardingData = useSelector<MicrosoftOnboarding>(
    state => state.flyOutOnboarding.microsoftOnboardingForm
  )
  const { pathname } = useLocation()
  const isOnboardingRoute = pathname.split('/').includes('onboarding')
  const isExternalRoute = pathname.split('/').includes('external-apps')

  const isCompanyInfoFilled =
    microsoftOnboardingData?.filledCompanyInfo || false
  const handleSubmitAPI = async (
    status?: string,
    submitCall?: boolean,
    shouldShowSuccessMessage: boolean = false
  ) => {
    if (accountId) {
      const updateOnboarding = {
        ...values,
        ...(submitCall && {
          filledCloudSettings: true,
        }),
        ...(submitCall &&
          isCompanyInfoFilled && {
            submit: true,
          }),
      }
      const isLockEmpty =
        isEmpty(updateOnboarding?.lockMetadata?.lockedBy) ||
        isEmpty(updateOnboarding?.lockMetadata?.windowId)
      if (status === 'lockAcquire' || isLockEmpty) {
        // Use existing window.name or generate a new one if it doesn't exist
        if (!window.name) {
          window.name = uuidv4()
        }
        const newLastActivityAt = DateTime.now().toUTC().toISO()

        updateOnboarding.lockMetadata = {
          ...updateOnboarding.lockMetadata,
          windowId: window.name,
          lockedBy: userProfileId,
          lastActivityAt: newLastActivityAt,
          lockedByName: latestLockMetadata.lockedByName,
          lockByEmail: latestLockMetadata.lockByEmail,
          lockAcquisitionProbe: isLockEmpty ? undefined : true,
        }
      } else {
        updateOnboarding.lockMetadata = {
          ...updateOnboarding.lockMetadata,
          lockAcquisitionProbe: undefined,
        }
      }
      const updateSettings = {
        ...values,
        filledCloudSettings: true,
        filledCompanyInfo: true,
        submit: true,
        companyInfo: {
          ...values?.companyInfo,
          acceptedTermsAndConditions: true,
          companyName: values?.companyInfo?.companyName,
          companyIndustry: values?.companyInfo?.companyIndustry,
          companyWebsite: values?.companyInfo?.companyWebsite,
        },
      }
      await dispatch(
        sendMarketplaceOnboardingData(
          accountId,
          isOnboardingRoute ? updateOnboarding : updateSettings,
          CloudType.AZURE,
          shouldShowSuccessMessage
        )
      )
      isOnboardingRoute &&
        isEmpty(status) &&
        !isCompanyInfoFilled &&
        navigate('/flyout/onboarding/company-information')
    }
  }
  const handleContinueClick = async (
    shouldShowSuccessMessage: boolean = false
  ) => {
    // Run test connection before submitting
    let connectionStatus = values.cloudCredentials.connectionTested
    if (connectCloudAccountRef?.current) {
      connectionStatus = await connectCloudAccountRef.current.testConnection()
    }

    // Touch the connectionTested field if connection test failed
    if (connectionStatus === 'failure') {
      formikProps.setFieldTouched('cloudCredentials.connectionTested', true)
    }

    await formikProps.submitForm()
    touchCloudCredentialFieldsWithErrors(formikProps)

    // Check if connection test failed
    const hasConnectionError = connectionStatus === 'failure'

    if (Object.keys(formikProps.errors).length > 0 || hasConnectionError) {
      setExpandedSections({
        prerequisites: !!formikProps.errors.prerequisites,
        accountConfiguration: !!formikProps.errors.accountConfiguration,
        cloudCredentials:
          !!formikProps.errors.cloudCredentials || hasConnectionError,
      })
      return
    }

    if (formikProps.isValid && !hasConnectionError) {
      await handleSubmitAPI(undefined, true, shouldShowSuccessMessage)
    }
  }
  const autoSaving = async (
    accountId: string,
    currentValues: FlyoutCloudSettingsSectionsProps
  ) => {
    const updatedValues = {
      ...currentValues,
      lockMetadata: {
        ...currentValues.lockMetadata,
        ...latestLockMetadata,
        lastActivityAt: latestLockMetadata.lastActivityAt,
      },
    }
    dispatch(
      autoSave('flyOutMicrosoftOnboarding', async () => {
        return await dispatch(
          sendMarketplaceOnboardingData(
            accountId,
            updatedValues,
            CloudType.AZURE
          )
        )
      })
    )
  }

  const debouncedAutoSave = useCallback(debounce(autoSaving, 5000), [
    autoSaving,
  ])

  const hasFormChanges = useCallback(() => {
    if (isFirstRender.current) {
      return false
    }

    // Convert current values to the same format as microsoftOnboardingData
    const currentFormData = {
      hasAccessedMpc: values.prerequisites.hasAccessedMpc,
      hasEnrolledInCommercialMarketplace:
        values.prerequisites.hasEnrolledInCommercialMarketplace,
      hasCompletedOrganizationProfile:
        values.prerequisites.hasCompletedOrganizationProfile,
      sellerType: values.accountConfiguration.sellerType,
      currencies: values.accountConfiguration.currencies,
      connectionName: values.cloudCredentials.connectionName,
      tenantId: values.cloudCredentials.tenantId,
      clientId: values.cloudCredentials.clientId,
      clientSecret: values.cloudCredentials.clientSecret,
      clientSecretExpirationDate:
        values.cloudCredentials.clientSecretExpirationDate,
      mpnId: values.cloudCredentials.mpnId,
    }

    // Compare with previous saved state (excluding lock metadata and connectionTested)
    const previousFormData = {
      hasAccessedMpc: microsoftOnboardingData.hasAccessedMpc,
      hasEnrolledInCommercialMarketplace:
        microsoftOnboardingData.hasEnrolledInCommercialMarketplace,
      hasCompletedOrganizationProfile:
        microsoftOnboardingData.hasCompletedOrganizationProfile,
      sellerType: microsoftOnboardingData.sellerType,
      currencies: microsoftOnboardingData.currencies,
      connectionName: microsoftOnboardingData.connectionName,
      tenantId: microsoftOnboardingData.tenantId,
      clientId: microsoftOnboardingData.clientId,
      clientSecret: microsoftOnboardingData.clientSecret,
      clientSecretExpirationDate:
        microsoftOnboardingData.clientSecretExpirationDate,
      mpnId: microsoftOnboardingData.mpnId,
    }

    return !isEqual(currentFormData, previousFormData)
  }, [values, microsoftOnboardingData, isFirstRender])

  useEffect(() => {
    if (!hasFormChanges()) {
      isFirstRender.current = false
      return
    }

    isFirstRender.current = false

    if (accountId && isOnboardingRoute) {
      debouncedAutoSave(accountId, values)
    }
    return () => {
      debouncedAutoSave.cancel()
    }
  }, [accountId, values, dispatch, hasFormChanges, isOnboardingRoute])

  type FooterType = 'ONBOARDING' | 'COMPLETED'

  let footerType: FooterType = 'ONBOARDING'
  if (isOnboardingRoute) {
    footerType = 'ONBOARDING'
  } else {
    footerType = 'COMPLETED'
  }
  return (
    <>
      {footerType === 'ONBOARDING' ? (
        <FooterV2
          actions={
            <div className={classes.onboardingActions}>
              {lockStatus !== '' ? (
                <LockMessageState
                  sinceTime={formikProps?.values?.lockMetadata?.lastActivityAt}
                  lockByName={formikProps?.values?.lockMetadata?.lockedByName}
                  lockStatus={lockStatus}
                  setLockStatus={setLockStatus}
                  userId={userProfileId}
                  lockedBy={formikProps?.values?.lockMetadata?.lockedBy}
                  lockByEmail={formikProps?.values?.lockMetadata?.lockByEmail}
                />
              ) : !autoSaveStatus || autoSaveStatus.status === 'standBy' ? (
                <div></div>
              ) : (
                <div className={classes.autosave}>
                  <AutoSaveFeedback
                    status={autoSaveStatus?.status || 'standBy'}
                  />
                </div>
              )}
              <div className={classes.btnWrapper}>
                <>
                  {lockStatus !== footerStateType.UNLOCKED ? (
                    <>
                      <LabraToolTipV2
                        tooltipPlacement="top-end"
                        description={disableTooltipMsg}
                        tooltipPlacmentGap="32px"
                      >
                        <div className={isFormLocked ? classes.disableBtn : ''}>
                          <ButtonV2
                            styleType="outlined"
                            data-testid={'Back-button'}
                            onClick={() => navigate(-1)}
                            disabled={isFormLocked}
                          >
                            Back
                          </ButtonV2>
                        </div>
                      </LabraToolTipV2>
                      <LabraToolTipV2
                        tooltipPlacement="top-end"
                        description={disableTooltipMsg}
                        tooltipPlacmentGap="32px"
                      >
                        <div className={isFormLocked ? classes.disableBtn : ''}>
                          <ButtonV2
                            data-testid="continue-button-flyout-microsoft-onboarding"
                            styleType="primary"
                            onClick={() => handleContinueClick(false)}
                            disabled={isFormLocked}
                            loading={false}
                            type="submit"
                          >
                            Continue
                          </ButtonV2>
                        </div>
                      </LabraToolTipV2>
                    </>
                  ) : (
                    <>
                      <ButtonV2
                        data-testid="save-button-flyout-onboarding-put"
                        styleType="primary"
                        onClick={() => handleSubmitAPI('lockAcquire', false)}
                        loading={false}
                      >
                        Start editing
                      </ButtonV2>
                    </>
                  )}
                </>
              </div>
            </div>
          }
          footerState={lockStatus}
          customClass={classes.root}
        />
      ) : (
        <FooterV2
          actions={
            <div className={classes.btnWrapper}>
              <>
                <ButtonV2
                  styleType="outlined"
                  data-testid="back-button-microsoft-cloud-settings"
                  onClick={() => navigate(-1)}
                >
                  Back
                </ButtonV2>
                <>
                  <ButtonV2
                    data-testid="save-button-flyout-settings-put"
                    styleType="primary"
                    onClick={() => handleContinueClick(true)}
                    type="submit"
                    loading={false}
                  >
                    Save
                  </ButtonV2>
                </>
              </>
            </div>
          }
          customClass={classes.root}
          width={isExternalRoute ? 'calc(100% + 8px)' : undefined}
        />
      )}
    </>
  )
}
